import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import AccordionReact from "devextreme-react/accordion";
import { Button } from "devextreme-react/button";
import Toolbar, { Item } from "devextreme-react/toolbar";
import DataSource from "devextreme/data/data_source";
import { createStore } from "devextreme-aspnet-data-nojquery";
import DataGrid, {
    Column,
    ColumnChooser,
    ColumnChooserSearch,
    Export,
    FilterPanel,
    FilterRow,
    HeaderFilter,
    Lookup,
    Pager,
    Paging,
    Selection,
    Sorting,
    StateStoring,
    Toolbar as GridToolbar,
    Item as GridToolbarItem,
} from "devextreme-react/data-grid";
import FormField from "../../../../dynamicFormBuilder/FormField";
import GridLayoutManager from "../../../../gridLayoutCustomization/GridLayoutManager";
import { GridLayout } from "../../../../common/GridLayout";
import { buildDxFilters, loadLookupFromStaticDataSource } from "../../../../common/Utils";
import { useSearchState } from "../../../../common/SearchStatePersistence";
import { TransManagementProfileRoutes } from "../../../../const/constants";
import data from "../../../../assets/apiRoutes.json";
import { SearchLayout } from "./SearchLayout";
import {
    COLOR_OPTIONS,
    DEFAULT_TAB_OPTIONS,
    FIELD_OPTIONS,
    ITEM_BASIS_QTY_OPTIONS,
    PAGE_TITLE,
    STATUS_OPTIONS,
    TRANS_BASIS_DATE_OPTIONS,
    VISIBLE_TABS_OPTIONS,
    YES_NO_OPTIONS,
} from "./CodeLists";
import { applyColorFill, decorateColorSelect, isColorField } from "./ColorSwatch";
import { deleteTransManagementProfile } from "./deleteProfile";
import "../../../../common/css/searchPage.css";

// FIND screen for Delphi WMD0081 "WM Transaction Mgmt Profile" (browse of WMTRANSMANAGEMENTDP).
// Accordion search panel driven by the shared dynamic form, plus a results grid whose layout is
// saved per user via GridLayoutManager.
//
// Every coded column is a <Lookup> here and a select in the search panel (rule 3), reading the same
// lists from CodeLists.js. The Transactions-tab and Items-tab colour columns share captions in the
// Delphi DisplayLabels ("Use Color", "Color Text" appear on both tabs), so the grid prefixes them
// with the tab they belong to — otherwise the column chooser shows two identically named columns.
const ROUTES = data.TransManagementDp;
const searchpanel = [{ ID: 1, Title: "Search Filters" }];
const allowedPageSizes = [10, 20, 100, 500, "all"];
const gridKey = "8d4f27b1-0a63-4c95-9f1e-6b2d81c07a34";

// Rule 17: the search survives one round trip into the Add/Edit form and is dropped on any other
// navigation or a reload.
const SEARCH_STATE_PAGE_KEY = "wm.transManagementProfile";
const SEARCH_STATE_DETAIL_PATHS = [
    TransManagementProfileRoutes.Create,
    TransManagementProfileRoutes.Edit,
];

// One key per search-schema field so the controlled inputs stay controlled.
const buildInitialFields = () => {
    const init = {};
    SearchLayout.forEach((g) => g.fields.forEach((f) => { init[f.name] = ""; }));
    return init;
};

// The date criteria each bound a range on one audit column, so they are left out of buildDxFilters
// (which filters a criterion on the column of the same name) and added as their own conditions.
const DATE_RANGES = [
    { column: "CreationDateTime", from: "CreationDateFrom", to: "CreationDateTo" },
    { column: "LastUpdateDateTime", from: "LastUpdateDateFrom", to: "LastUpdateDateTo" },
];
const DATE_CRITERIA = DATE_RANGES.flatMap(({ from, to }) => [from, to]);

const midnight = (value, addDays = 0) => {
    const date = new Date(value);
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + addDays);
    return date;
};

/**
 * The search panel's criteria as a DevExtreme filter, which DataSourceLoader applies on the server —
 * the same built-in path the grid's filter row uses. buildDxFilters reads each criterion's type from
 * the layout: text is a `contains` match, a select is an exact match on its code.
 */
const buildSearchFilter = (fields) => {
    const criteria = fields || {};
    const filters = buildDxFilters(criteria, SearchLayout, DATE_CRITERIA);
    const and = (condition) => {
        if (filters.length > 0) filters.push("and");
        filters.push(condition);
    };

    // The pickers are date-only, so "To" is an exclusive next-midnight bound: compared as `<=` against
    // midnight it would drop every record stamped later on the day the user picked.
    DATE_RANGES.forEach(({ column, from, to }) => {
        if (criteria[from]) and([column, ">=", midnight(criteria[from])]);
        if (criteria[to]) and([column, "<", midnight(criteria[to], 1)]);
    });

    return filters.length > 0 ? filters : null;
};

function List(props) {
    const accessToken = props.oidc?.user?.access_token;
    const currentUser = props.oidc?.user;
    const dynamicFormSetting = props.oidc?.user?.profile?.DYNAMICFORMSETTING;
    const history = useHistory();

    const dataGrid = useRef(null);
    const rowId = useRef("");
    const formRefs = useRef({});

    const [resultData, setResultData] = useState(null);
    const [selectedItems, setSelectedItems] = useState([]);
    const [searchPanelStatus, setSearchPanelStatus] = useState(false);
    const [isRowSelected, setIsRowSelected] = useState(false);
    const [currentLayout, setCurrentLayout] = useState("Default");

    const initialFormFields = useMemo(() => buildInitialFields(), []);

    // Grid *layout* is a durable preference and stays in the database; the *filter* is transient and
    // is handled by useSearchState (rule 17).
    const saveState = useCallback((state) => {
        GridLayout.sendStorageRequest(gridKey, "POST", accessToken, state, currentLayout);
    }, [accessToken, currentLayout]);
    const loadState = useCallback(() => {
        return GridLayout.sendStorageRequest(gridKey, "GET", accessToken, null, currentLayout);
    }, [accessToken, currentLayout]);

    const {
        formFields,
        setformFields,
        rememberSearch,
        forgetSearch,
        loadGridState,
        saveGridState,
    } = useSearchState({
        pageKey: SEARCH_STATE_PAGE_KEY,
        initialFormFields,
        layout: SearchLayout,
        detailPaths: SEARCH_STATE_DETAIL_PATHS,
        enabled: !props.pickerMode,
        gridRef: dataGrid,
        loadGridLayoutState: loadState,
        saveGridLayoutState: saveState,
        gridLayoutName: currentLayout,
    });

    // Built from the criteria handed in, not from a closure over formFields, so Reset can rebuild the
    // grid from the empty criteria without waiting for the state update to land.
    const loadDatasource = (fields) => {
        const store = createStore({
            key: "RowId",
            loadUrl: `${window._env_.REACT_APP_APIS}/${ROUTES.GetTransManagementDps}`,
            onBeforeSend: (method, ajaxOptions) => {
                ajaxOptions.headers = {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                };
            },
        });
        const dataSource = new DataSource({ store });
        dataSource.filter(buildSearchFilter(fields));
        return dataSource;
    };

    const applySearch = useCallback((fields) => {
        setResultData(loadDatasource(fields));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken]);

    // formFields already carries whatever was remembered from the previous visit, so the first pass
    // re-applies it — no extra fetch and no flash of unfiltered rows.
    useEffect(() => {
        applySearch(formFields);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Rule 3: the search panel's dropdowns get their lists the way the dynamic form expects — through
    // formRefs, in onFormLoad. Driven off the layout so a new criterion needs no change here.
    const handleFormLoad = useCallback(() => {
        setTimeout(() => {
            SearchLayout.forEach((group) => group.fields.forEach((field) => {
                const options = FIELD_OPTIONS[field.name];
                if (field.type !== "select" || !options) return;

                loadLookupFromStaticDataSource(field.name, options, null, formRefs, "id", "text");

                // Colour criteria show the colour itself here too, so the search panel reads the same
                // as the form the results open into.
                if (isColorField(field.name)) decorateColorSelect(formRefs.current?.[field.name]?.instance?.());
            }));
        }, 100);
    }, []);

    // A colour criterion's fill follows its value, the same way the form's does: picks reach
    // formFields through the dynamic form's onValueChanged, and Reset replaces them there too.
    // The colour criteria start hidden (SearchLayout's per-group cap); for those there is no widget
    // and this does nothing until one is dragged back into a group.
    useEffect(() => {
        [
            ["TransTabPendingColor", formFields.TransTabPendingColor],
            ["TransTabWarningColor", formFields.TransTabWarningColor],
            ["ItemTabPendingColor", formFields.ItemTabPendingColor],
            ["ItemTabWarningColor", formFields.ItemTabWarningColor],
        ].forEach(([name, code]) => applyColorFill(formRefs.current?.[name]?.instance?.(), code));
    }, [
        formFields.TransTabPendingColor,
        formFields.TransTabWarningColor,
        formFields.ItemTabPendingColor,
        formFields.ItemTabWarningColor,
    ]);

    const selectionChanged = useCallback((e) => {
        let newItems = [...selectedItems];
        e.removedItems.forEach((item) => { const i = newItems.indexOf(item); if (i >= 0) newItems.splice(i, 1); });
        if (e.addedItems.length) newItems = [...newItems, ...e.addedItems];
        setSelectedItems(newItems);
        setSearchPanelStatus((prev) => !prev);
    }, [selectedItems]);

    const handleSearch = (e) => {
        e.event.stopPropagation();
        applySearch(formFields);
        rememberSearch(formFields);
    };

    const handleReset = (e) => {
        e.event.stopPropagation();
        applySearch(forgetSearch());
    };

    const handleFieldChange = useCallback(() => {}, []);

    const onSelectionChangedGrid = ({ selectedRowsData }) => {
        const row = selectedRowsData && selectedRowsData.length === 1 ? selectedRowsData[0] : null;
        rowId.current = row ? row.RowId : "";
        setIsRowSelected(!!row);
    };

    const handleCreateClick = () => history.push(TransManagementProfileRoutes.Create);
    const handleEditClick = () => {
        if (!rowId.current) return;
        history.push(`${TransManagementProfileRoutes.Edit}?id=${rowId.current}`);
    };

    const handleDeleteClick = (e) => {
        // Sits on the accordion title bar: without this the click also collapses the search panel.
        if (e?.event) e.event.stopPropagation();
        if (!rowId.current) return;

        deleteTransManagementProfile(rowId.current, accessToken).then((deleted) => {
            if (!deleted) return;

            // The grid drops the row.
            rowId.current = "";
            setIsRowSelected(false);
            if (dataGrid.current?.instance) dataGrid.current.instance().refresh();
        });
    };

    const pager = useMemo(() => ({
        visible: true, showPageSizeSelector: true, allowedPageSizes, showNavigationButtons: true,
    }), []);

    return (
        <div className="search-page-container">
            <div className="search-page-content">
                <AccordionReact
                    dataSource={searchpanel}
                    collapsible={true}
                    multiple={false}
                    animationDuration={300}
                    selectedItems={selectedItems}
                    onSelectionChanged={(e) => { if (e.currentTarget === e.target) selectionChanged(e); }}
                    itemTitleRender={() => (
                        <Toolbar className="accordian-toolbar">
                            <Item location="before">
                                <div className="title">
                                    <h6 style={{ color: window._env_.Color2 }}>{PAGE_TITLE}</h6>
                                </div>
                            </Item>
                            <Item location="after">
                                <Button className="accordianButton" onClick={handleSearch} text="Search" type="default" />
                            </Item>
                            <Item location="after">
                                <Button className="accordianButton" onClick={handleDeleteClick} text="Delete" disabled={!isRowSelected} />
                            </Item>
                            <Item location="after">
                                <Button className="accordianButton" onClick={handleReset} text="Reset" />
                            </Item>
                        </Toolbar>
                    )}
                    itemRender={() => (
                        <div>
                            <FormField
                                currrentUser={currentUser}
                                layoutId="0081a5c7-3e42-4b18-8d76-0081aabb1122"
                                layoutName="WMTransManagementProfileSearchForm"
                                defaultFormSchema={SearchLayout}
                                formFields={formFields}
                                setformFields={setformFields}
                                handleFieldValueChange={handleFieldChange}
                                DYNAMICFORMSETTING={dynamicFormSetting}
                                formRefs={formRefs}
                                onFormLoad={handleFormLoad}
                                noSpacingBeforeFirstRow={true}
                            />
                        </div>
                    )}
                    id="accordion-container"
                    className="accordion-height"
                />

                <DataGrid
                    ref={dataGrid}
                    dataSource={resultData}
                    remoteOperations={true}
                    showBorders={true}
                    hoverStateEnabled={true}
                    allowColumnReordering={true}
                    allowColumnResizing={true}
                    columnAutoWidth={true}
                    className={searchPanelStatus ? "GridHeightAccordianOpen" : "GridHeightAccordianClose"}
                    onSelectionChanged={onSelectionChangedGrid}
                    onExporting={(e) => { if (window.handleDevExtremeDataGridExport) window.handleDevExtremeDataGridExport(e); }}
                >
                    <ColumnChooser enabled={true} title="Choose Column" width={290} height={290} sortOrder="asc">
                        <ColumnChooserSearch enabled={true} editorOptions={{ placeholder: "Search column" }} />
                    </ColumnChooser>
                    <StateStoring enabled={true} type="custom" customLoad={loadGridState} storageKey={gridKey} customSave={saveGridState} />
                    <Export enabled={true} allowExportSelectedData={true} />
                    <Selection mode="multiple" />
                    <Sorting mode="multiple" />
                    <FilterRow visible={true} />
                    <HeaderFilter visible={true} />
                    <FilterPanel visible={true} />
                    <Paging defaultPageSize={20} />
                    <Pager {...pager} />

                    {/* Rule 8: the technical key is hidden. Rule 10: Record # is exposed read-only. */}
                    <Column dataField="RowId" caption="Row Id" visible={false} showInColumnChooser={false} />
                    <Column dataField="RecordNumber" caption="Record #" dataType="string" />
                    <Column dataField="ProfileCode" caption="Profile Code" dataType="string" />
                    <Column dataField="Description" caption="Description" dataType="string" />
                    <Column dataField="Status" caption="Status" dataType="string">
                        <Lookup dataSource={STATUS_OPTIONS} displayExpr="text" valueExpr="id" />
                    </Column>

                    {/* Trans Mgmt tab */}
                    <Column dataField="TabDefaults" caption="Visible Tabs" dataType="string">
                        <Lookup dataSource={VISIBLE_TABS_OPTIONS} displayExpr="text" valueExpr="id" />
                    </Column>
                    <Column dataField="TransTabDefaults" caption="Default Tab" dataType="string">
                        <Lookup dataSource={DEFAULT_TAB_OPTIONS} displayExpr="text" valueExpr="id" />
                    </Column>
                    <Column dataField="EdiOrdersTab" caption="EDI Tab" dataType="string" visible={false}>
                        <Lookup dataSource={YES_NO_OPTIONS} displayExpr="text" valueExpr="id" />
                    </Column>
                    <Column dataField="WebOrdersTab" caption="Web Tab" dataType="string" visible={false}>
                        <Lookup dataSource={YES_NO_OPTIONS} displayExpr="text" valueExpr="id" />
                    </Column>
                    <Column dataField="CustomersTab" caption="Customer Tab" dataType="string" visible={false}>
                        <Lookup dataSource={YES_NO_OPTIONS} displayExpr="text" valueExpr="id" />
                    </Column>
                    <Column dataField="ItemTab" caption="Item Tab" dataType="string" visible={false}>
                        <Lookup dataSource={YES_NO_OPTIONS} displayExpr="text" valueExpr="id" />
                    </Column>
                    <Column dataField="LotTab" caption="Lot Tab" dataType="string" visible={false}>
                        <Lookup dataSource={YES_NO_OPTIONS} displayExpr="text" valueExpr="id" />
                    </Column>
                    <Column dataField="SubLotTab" caption="Sublot Tab" dataType="string" visible={false}>
                        <Lookup dataSource={YES_NO_OPTIONS} displayExpr="text" valueExpr="id" />
                    </Column>
                    <Column dataField="LocationTab" caption="Location Tab" dataType="string" visible={false}>
                        <Lookup dataSource={YES_NO_OPTIONS} displayExpr="text" valueExpr="id" />
                    </Column>
                    <Column dataField="UninvoicedChargesTab" caption="Un Inv Tab" dataType="string" visible={false}>
                        <Lookup dataSource={YES_NO_OPTIONS} displayExpr="text" valueExpr="id" />
                    </Column>

                    {/* Transactions tab colour thresholds */}
                    <Column dataField="TransTabUseColors" caption="Trans Use Color" dataType="string" visible={false}>
                        <Lookup dataSource={YES_NO_OPTIONS} displayExpr="text" valueExpr="id" />
                    </Column>
                    <Column dataField="TransTabBasisDate" caption="Trans Basis Date" dataType="string" visible={false}>
                        <Lookup dataSource={TRANS_BASIS_DATE_OPTIONS} displayExpr="text" valueExpr="id" />
                    </Column>
                    <Column dataField="TransTabPendingLevel" caption="Trans Hours" dataType="number" format="#,###0.00" visible={false} />
                    <Column dataField="TransTabPendingColor" caption="Trans Color Text" dataType="string" visible={false}>
                        <Lookup dataSource={COLOR_OPTIONS} displayExpr="text" valueExpr="id" />
                    </Column>
                    <Column dataField="TransTabWarningLevel" caption="Trans Warning Hours" dataType="number" format="#,###0.00" visible={false} />
                    <Column dataField="TransTabWarningColor" caption="Trans Warning Color" dataType="string" visible={false}>
                        <Lookup dataSource={COLOR_OPTIONS} displayExpr="text" valueExpr="id" />
                    </Column>

                    {/* Items tab colour thresholds */}
                    <Column dataField="ItemTabUseColors" caption="Item Use Color" dataType="string" visible={false}>
                        <Lookup dataSource={YES_NO_OPTIONS} displayExpr="text" valueExpr="id" />
                    </Column>
                    <Column dataField="ItemTabBasisQty" caption="Item Basis Qty" dataType="string" visible={false}>
                        <Lookup dataSource={ITEM_BASIS_QTY_OPTIONS} displayExpr="text" valueExpr="id" />
                    </Column>
                    <Column dataField="ItemTabPendingLevel" caption="Item Quantity" dataType="number" format="#,###0.00" visible={false} />
                    <Column dataField="ItemTabPendingColor" caption="Item Color Text" dataType="string" visible={false}>
                        <Lookup dataSource={COLOR_OPTIONS} displayExpr="text" valueExpr="id" />
                    </Column>
                    <Column dataField="ItemTabWarningLevel" caption="Item Warning Qty" dataType="number" format="#,###0.00" visible={false} />
                    <Column dataField="ItemTabWarningColor" caption="Item Warning Color" dataType="string" visible={false}>
                        <Lookup dataSource={COLOR_OPTIONS} displayExpr="text" valueExpr="id" />
                    </Column>

                    {/* COMMENT is a SQL Server `text` column: it can be selected and LIKE-matched but not
                        ordered or grouped, so sorting it raises "The text, ntext, and image data types
                        cannot be compared or sorted". The header filter hits the same error — with
                        remoteOperations it loads its value list as a GROUP BY on the column. Both are
                        disabled rather than hidden behind an error. */}
                    <Column dataField="Comment" caption="Comment" dataType="string" allowSorting={false} allowHeaderFiltering={false} visible={false} />

                    {/* Audit columns — the projection already carries them. */}
                    <Column dataField="CreationDateTime" caption="Creation Date" dataType="datetime" format="MM/dd/yyyy hh:mm a" visible={false} />
                    <Column dataField="CreationUserId" caption="Created By" dataType="string" visible={false} />
                    <Column dataField="LastUpdateDateTime" caption="Last Updated" dataType="datetime" format="MM/dd/yyyy hh:mm a" visible={false} />
                    <Column dataField="LastUpdateUserId" caption="Last Updated By" dataType="string" visible={false} />

                    <GridToolbar>
                        <GridToolbarItem location="before">
                            <Button onClick={handleEditClick} text="Edit" type="default" disabled={!isRowSelected} />
                        </GridToolbarItem>
                        <GridToolbarItem location="before">
                            <Button onClick={handleCreateClick} text="Create" type="default" className="gridTopButton" />
                        </GridToolbarItem>
                        <GridToolbarItem location="after">
                            <GridLayoutManager gridKey={gridKey} dataGrid={dataGrid} setCurrentLayout={setCurrentLayout} />
                        </GridToolbarItem>
                        <GridToolbarItem name="exportButton" />
                        <GridToolbarItem name="columnChooserButton" />
                    </GridToolbar>
                </DataGrid>
            </div>
        </div>
    );
}

function mapStateToProps(state) {
    return { oidc: state.oidc };
}

export default connect(mapStateToProps)(List);
